import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, FileText, PieChart, ArrowRight, CheckCircle } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
export function BusinessFinance() {
  const services = [{
    title: 'Accounting Services',
    icon: <Calculator size={32} data-id="element-163" />,
    items: ['Bookkeeping & Data Entry', 'Financial Statements Preparation', 'Cash Flow Management', 'Budgeting & Forecasting', 'Financial Analysis', 'Management Reporting']
  }, {
    title: 'Tax Services',
    icon: <FileText size={32} data-id="element-164" />,
    items: ['Corporate Tax Compliance', 'Individual Tax & Expat Tax', 'VAT (Value Added Tax) Reporting', 'Payroll Tax Management', 'Coretax System Integration', 'NPWP Registration', 'Strategic Tax Planning', 'Tax Consultation & Advisory']
  }, {
    title: 'Audit Services',
    icon: <PieChart size={32} data-id="element-165" />,
    items: ['Internal Audit & Process Review', 'Independent External Audit', 'Financial Review & Due Diligence', 'Risk Assessment', 'Compliance Audit', 'Operational Audit', 'Management Improvement Advisory']
  }];
  return <div className="bg-navy-900 min-h-screen" data-id="element-166">
      <PageHeader title="Accounting, Tax & Audit" subtitle="Rigorous financial management and strategic advisory to secure your corporate health and ensure full compliance in Indonesia." backgroundImage="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" data-id="element-167" />

      <section className="py-24" data-id="element-168">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-169">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-170">
            <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-171">
              Financial Excellence & Transparency
            </h2>
            <p className="font-body text-gray-400 text-lg leading-relaxed" data-id="element-172">
              Navigating Indonesia's financial and tax regulations requires
              precision and deep local knowledge. Our team of certified
              accountants and tax consultants provides enterprise-level
              financial services, ensuring your business remains compliant,
              efficient, and primed for growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-173">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} className="bg-navy-800 p-10 rounded-card border border-navy-700 hover:border-gold/30 transition-colors flex flex-col" data-id="element-174">
                <div className="w-16 h-16 bg-navy-700 rounded-2xl flex items-center justify-center text-gold mb-8" data-id="element-175">
                  {service.icon}
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-6" data-id="element-176">
                  {service.title}
                </h3>
                <ul className="space-y-4 flex-grow" data-id="element-177">
                  {service.items.map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 font-body text-sm" data-id="element-178">
                      <CheckCircle size={18} className="text-gold shrink-0 mt-0.5" data-id="element-179" />
                      <span data-id="element-180">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Dashboard Mockup Section */}
      <section className="py-24 bg-navy-800 border-y border-navy-700 overflow-hidden" data-id="element-181">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-182">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-183">
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="w-full lg:w-1/2" data-id="element-184">
              <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-185">
                Clear Insights, Better Decisions
              </h2>
              <p className="font-body text-gray-400 text-lg leading-relaxed mb-8" data-id="element-186">
                We transform complex financial data into clear, actionable
                insights. Our management reporting provides you with a
                comprehensive view of your company's financial health, enabling
                strategic decision-making.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-187">
                {['Real-time financial tracking', 'Customized KPI dashboards', 'Secure cloud-based accounting', 'Multi-currency reporting'].map((feature, i) => <li key={i} className="flex items-center gap-3 text-white font-sub" data-id="element-188">
                    <div className="w-2 h-2 bg-gold rounded-full" data-id="element-189"></div>
                    {feature}
                  </li>)}
              </ul>
              <Button to="/contact" data-id="element-190">Speak with an Advisor</Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="w-full lg:w-1/2" data-id="element-191">
              <div className="relative rounded-card overflow-hidden shadow-card border border-navy-700 bg-navy-900 p-2" data-id="element-192">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2015&q=80" alt="Financial Dashboard" className="w-full h-auto rounded-xl opacity-80" data-id="element-193" />
                <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/60 to-transparent pointer-events-none" data-id="element-194"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 text-center" data-id="element-195">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-196">
          <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-197">
            Ensure Your Financial Compliance Today
          </h2>
          <p className="font-body text-gray-400 text-lg mb-10" data-id="element-198">
            Partner with Marvesta Corp Bali for reliable accounting, strategic
            tax planning, and comprehensive audit services.
          </p>
          <Button to="/contact" icon={<ArrowRight size={18} data-id="element-200" />} data-id="element-199">
            Request Financial Consultation
          </Button>
        </div>
      </section>
    </div>;
}